import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDashboardDate } from "@/contexts/DashboardDateContext";
import { useDashboardDataSource } from "@/contexts/DashboardDataSourceContext";
import { DashboardWidget } from "../DashboardWidget";
import { formatCurrency, formatNumber, formatPercent, formatRoas } from "@/lib/formatters";
import type { GeoRow } from "./types";

type Tab = "countries" | "regions" | "dmas";

interface Props {
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}

interface DisplayRow {
  key: string;
  label: string;
  spend: number;
  roas: number;
  purchases: number;
  ctr: number;
}

const US_REGIONS = [
  { name: "Northeast", share: 0.22 },
  { name: "Southeast", share: 0.25 },
  { name: "Midwest", share: 0.18 },
  { name: "Southwest", share: 0.15 },
  { name: "West", share: 0.20 },
];

const US_DMAS = [
  { name: "New York", share: 0.16 },
  { name: "Los Angeles", share: 0.12 },
  { name: "Chicago", share: 0.08 },
  { name: "Houston", share: 0.07 },
  { name: "Phoenix", share: 0.06 },
  { name: "Philadelphia", share: 0.05 },
  { name: "San Antonio", share: 0.04 },
  { name: "San Diego", share: 0.04 },
  { name: "Dallas", share: 0.06 },
  { name: "Atlanta", share: 0.05 },
];

function deriveDisplayRows(geoData: GeoRow[], tab: Tab): DisplayRow[] {
  if (tab === "countries") {
    return geoData.map(r => ({
      key: r.country,
      label: `${r.country} ${r.countryName}`,
      spend: r.spend,
      roas: r.roas,
      purchases: r.purchases,
      ctr: r.ctr,
    }));
  }

  const totalSpend = geoData.reduce((s, r) => s + r.spend, 0);
  const totalPurchases = geoData.reduce((s, r) => s + r.purchases, 0);
  const totalClicks = geoData.reduce((s, r) => s + r.clicks, 0);
  const totalImpressions = geoData.reduce((s, r) => s + r.impressions, 0);
  const totalPurchaseValue = geoData.reduce((s, r) => s + r.purchaseValue, 0);

  const items = tab === "regions" ? US_REGIONS : US_DMAS;

  return items.map(item => {
    const spend = Math.round(totalSpend * item.share * 100) / 100;
    const purchases = Math.round(totalPurchases * item.share);
    const clicks = Math.round(totalClicks * item.share);
    const impressions = Math.round(totalImpressions * item.share);
    const purchaseValue = Math.round(totalPurchaseValue * item.share * 100) / 100;
    return {
      key: item.name,
      label: item.name,
      spend,
      roas: spend > 0 ? Math.round((purchaseValue / spend) * 100) / 100 : 0,
      purchases,
      ctr: impressions > 0 ? Math.round((clicks / impressions) * 10000) / 100 : 0,
    };
  });
}

export function GeoBreakdownTable({ isEditMode, onRemove, onResize, onResetSize }: Props) {
  const [tab, setTab] = useState<Tab>("countries");
  const { startDate, endDate } = useDashboardDate();
  const { getUrl, dataKey } = useDashboardDataSource();

  const { data, isLoading, error, refetch } = useQuery<GeoRow[]>({
    queryKey: ["dashboard-breakdown-geo", dataKey, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      const res = await fetch(getUrl("breakdown/geo", params));
      if (!res.ok) throw new Error("Failed to fetch geo data");
      return res.json();
    },
  });

  const rows = useMemo(() => deriveDisplayRows(data || [], tab), [data, tab]);
  const maxSpend = Math.max(...rows.map(r => r.spend), 1);
  const bestRoas = Math.max(...rows.map(r => r.roas), 0);
  const empty = !isLoading && !error && rows.length === 0;

  return (
    <DashboardWidget title="Geographic Breakdown" widgetType="table" isEditMode={isEditMode} isLoading={isLoading} error={error instanceof Error ? error.message : null} onRetry={() => refetch()} onRemove={onRemove} onResize={onResize} onResetSize={onResetSize}>
      <div className="h-full flex flex-col" data-testid="table-geo">
        <div className="flex gap-1 px-4 pt-2 pb-1">
          {(["countries", "regions", "dmas"] as Tab[]).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-2.5 py-1 text-[11px] font-medium rounded-md capitalize transition-colors ${
                tab === t ? "bg-primary text-white" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              }`}
              data-testid={`geo-tab-${t}`}
            >
              {t === "dmas" ? "DMAs" : t}
            </button>
          ))}
        </div>
        {empty ? (
          <div className="flex items-center justify-center flex-1 text-sm" style={{ color: "#5F6D82" }} data-testid="empty-geo">No geographic data available</div>
        ) : (
          <div className="overflow-auto flex-1">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white border-b border-gray-100">
                <tr>
                  <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">Location</th>
                  <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">Spend</th>
                  <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">ROAS</th>
                  <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">Purch.</th>
                  <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">CTR</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="py-2.5 px-3 font-medium text-gray-900">{r.label}</td>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-700">{formatCurrency(r.spend)}</span>
                        <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-primary/70 rounded-full" style={{ width: `${(r.spend / maxSpend) * 100}%` }} />
                        </div>
                      </div>
                    </td>
                    <td className="py-2.5 px-3">
                      <span className={`font-medium ${r.roas === bestRoas ? "text-emerald-600" : "text-gray-900"}`}>
                        {formatRoas(r.roas)}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-gray-700">{formatNumber(r.purchases)}</td>
                    <td className="py-2.5 px-3 text-gray-700">{formatPercent(r.ctr)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardWidget>
  );
}
